import {VNode} from 'snabbdom';
import {jsx} from 'panel';

import {TemplateType} from 'common/report/dashboards/types';

export enum RHSPanelMode {
  StarterBoard = `starter-board`,
  Templates = `templates`,
}

export interface RHSPanelState {
  fields: {[key in TemplateField]?: any} | EmptyObj;
  calendarInputOpen?: boolean;
  tutorialCurrentStepIndex: number;
  showTutorial: boolean;
  hideTutorial: boolean;
}

export interface RHSPanelProps {
  params?: RHSPanelParams;
  on?: {
    updateBlueprint?: (ev: CustomEvent<any>) => void;
  };
}

export interface RHSPanelParams {
  mode: RHSPanelMode;
  selectedTemplate?: TemplateType;
  isLoading?: boolean;
}

export type MoreInfoConfig = {
  text: string;
  link: string;
};

export enum RHSInputType {
  Event = `event`,
  Property = `property`,
  Date = `date`,
  List = `list`,
  Key = `key`,
  Text = `text`,
}

export type TemplateCard = {
  title: string;
  templateSelectDescription: string;
  imageId: string;
  eventSelectStepDescription: string;
  requiredFieldsCheck: () => boolean;
  demoBoardLink?: string;
  moreInfo?: MoreInfoConfig;
};

export type TemplateField =
  | KPITemplateFields
  | ProductLaunchTemplateFields
  | LifecycleCohortAnalysisTemplateFields
  | MarketingKPITemplateFields
  | ECommerceTemplateFields
  | WebAnalyticsTemplateFields
  | BenchmarkKPITemplateFields;

export enum KPITemplateFields {
  SIGN_UP = `Sign Up`,
  VALUE_MOMENT = `Value Moment`,
}

export enum BenchmarkKPITemplateFields {
  SIGN_UP = `Sign Up`,
  VALUE_MOMENT = `Value Moment`,
  INDUSTRY = `Industry`,
}

export enum ProductLaunchTemplateFields {
  FEATURE_NAME = `Feature Name`,
  LAUNCH_DATE = `Launch Date`,
  NEW_FEATURE = `New Feature`,
  FEATURE_VALUE_MOMENT = `Feature Value Moment`,
}

export enum LifecycleCohortAnalysisTemplateFields {
  VALUE_MOMENT = `Value Moment`,
}

export enum MarketingKPITemplateFields {
  ACTIVATION_EVENT = `Activation Event`,
  CONVERSION_EVENT = `Conversion Event`,
}

export enum ECommerceTemplateFields {
  PURCHASE_EVENT = `Purchase`,
  ADD_TO_CART_EVENT = `Add to Cart`,
  PRODUCT_VIEWED_EVENT = `Product Viewed`,
  CART_PROPERTY = `Cart Property`,
  CART_PROPERTY_2 = `Cart Property 2`,
  PRICE_KEY = `Price Key`,
  CATEGORY_KEY = `Category Key`,
}

export enum WebAnalyticsTemplateFields {
  PAGE_LOADED_EVENT = `Page Loaded`,
  PAGE_NAME_PROPERTY = `Page Name`,
}

export type TutorialStep = {
  nextStepIndex?: number;
  stepInfo: VNode | string;
};

export type TUTORIAL_FINISHED = `Tutorial Finished`;

export type TutorialConfig = {
  [key in TemplateField | TUTORIAL_FINISHED]?: TutorialStep;
};

export const ECOMMERCE_TUTORIAL_CONFIG: TutorialConfig = {
  [ECommerceTemplateFields.PURCHASE_EVENT]: {
    nextStepIndex: 1,
    stepInfo: (
      <div>
        First, add your <strong>Purchase Event</strong>. This is the moment a user transacts on your website.
      </div>
    ),
  },
  [ECommerceTemplateFields.CART_PROPERTY]: {
    nextStepIndex: 2,
    stepInfo: (
      <div>
        <div>
          Now add your <strong>Cart Property</strong>
        </div>
        <div>
          <em>Example names: Shopping bag, Cart, Basket, Checkout</em>
        </div>
      </div>
    ),
  },
  [ECommerceTemplateFields.PRICE_KEY]: {
    nextStepIndex: 3,
    stepInfo: (
      <div>
        Now add your <strong>Price Property</strong>. This is a numeric property that represents how much an individual
        item costs.
      </div>
    ),
  },
  [ECommerceTemplateFields.ADD_TO_CART_EVENT]: {
    nextStepIndex: 4,
    stepInfo: (
      <div>
        Now add your <strong>Add to Cart Event</strong>. This is the action of a user moving a product into their
        shopping cart or bag.
      </div>
    ),
  },
  [ECommerceTemplateFields.CART_PROPERTY_2]: {
    nextStepIndex: 5,
    stepInfo: (
      <div>
        <div>
          Now add your <strong>Cart Property</strong>
        </div>
        <div>
          <em>Example names: Shopping bag, Cart, Basket, Checkout</em>
        </div>
      </div>
    ),
  },
  [ECommerceTemplateFields.CATEGORY_KEY]: {
    nextStepIndex: 6,
    stepInfo: (
      <div>
        <div>
          Now add your <strong>Category Property</strong>. This property can be anything to help further segment your
          products.
        </div>
        <div>
          <em>Ex. “Category” “Brand” “Type”</em>
        </div>
      </div>
    ),
  },
  [ECommerceTemplateFields.PRODUCT_VIEWED_EVENT]: {
    nextStepIndex: 7,
    stepInfo: (
      <div>
        Lastly, add your <strong>Product Viewed Event</strong>. This represents when a user views a specific item on
        your app or site.
      </div>
    ),
  },
  'Tutorial Finished': {
    stepInfo: `You’ve completed the E-commerce template walkthrough! 🎉`,
  },
};

export const ADVANCED_TEMPLATE_TYPES_TO_TUTORIAL_MAP: Map<TemplateType, TutorialConfig> = new Map([
  [TemplateType.E_COMMERCE, ECOMMERCE_TUTORIAL_CONFIG],
]); // add more advanced template types here
